import { useMemo } from 'react';
import { components, paths } from '@lifeserver/models/gift';
import { useQueryClient } from '@tanstack/react-query';
import createFetchClient, { ClientOptions } from 'openapi-fetch';
import createClient from 'openapi-react-query';

export type GiftSchemas = components['schemas'];

type GiftBody = GiftSchemas['PutGiftRequestDTO'];
type PublicationWindow = GiftSchemas['CreateGiftPublicationRequestDTO'];

const DEFAULT_OPTIONS: ClientOptions = { baseUrl: '/api' };
const GIFTS_QUERY_KEY = ['get', '/gift/api/v2/admin/gifts'];

/** openapi-fetch rejects with the parsed error body, not an Error. */
export const getApiErrorMessage = (error: unknown): string => {
    if (error instanceof Error) return error.message;
    if (
        typeof error === 'object' &&
        error !== null &&
        'message' in error &&
        typeof error.message === 'string'
    ) {
        return error.message;
    }
    return 'Unknown error';
};

export const useGiftApi = (options: ClientOptions = DEFAULT_OPTIONS) => {
    return useMemo(() => {
        const fetchClient = createFetchClient<paths>(options);
        const $api = createClient(fetchClient);
        return $api;
    }, [options]);
};

export const useGiftAdmin = () => {
    const giftApi = useGiftApi();
    const queryClient = useQueryClient();

    const upsertGift = giftApi.useMutation(
        'put',
        '/gift/api/v2/admin/gifts/{id}',
    );
    const createPublication = giftApi.useMutation(
        'post',
        '/gift/api/v2/admin/gifts/{id}/publications',
    );
    const removePublication = giftApi.useMutation(
        'delete',
        '/gift/api/v2/admin/gifts/{id}/publications/{publicationId}',
    );
    const changePublication = giftApi.useMutation(
        'put',
        '/gift/api/v2/admin/gifts/{id}/publications/{publicationId}',
    );

    const refreshGifts = () =>
        queryClient.invalidateQueries({ queryKey: GIFTS_QUERY_KEY });

    const updateGift = async (id: string, body: GiftBody) => {
        await upsertGift.mutateAsync({ params: { path: { id } }, body });
        await refreshGifts();
    };

    const publishGift = async (id: string, window: PublicationWindow) => {
        const publication = await createPublication.mutateAsync({
            params: { path: { id } },
            body: window,
        });
        await refreshGifts();
        return publication;
    };

    const createGift = (
        id: string,
        body: GiftBody,
        window: PublicationWindow,
    ) => updateGift(id, { ...body, publication: window });

    const deletePublication = async (id: string, publicationId: string) => {
        await removePublication.mutateAsync({
            params: { path: { id, publicationId } },
        });
        await refreshGifts();
    };

    const updatePublication = async (
        id: string,
        publicationId: string,
        window: PublicationWindow,
    ) => {
        await changePublication.mutateAsync({
            params: { path: { id, publicationId } },
            body: window,
        });
        await refreshGifts();
    };

    return {
        createGift,
        updateGift,
        publishGift,
        updatePublication,
        deletePublication,
        isSaving:
            upsertGift.isPending ||
            createPublication.isPending ||
            changePublication.isPending ||
            removePublication.isPending,
    };
};
